import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { DEFAULT_SEASON, ensureLeagueSeason, finalizeExpiredSeason, subscribeClubs, subscribeSeason } from "../services/leagueService";
import { buildStandings } from "../services/pointsEngine";
import { subscribeCompetitions } from "../services/competitionService";

function friendlyLeagueError(error) {
  const code = String(error?.code || "");
  const message = String(error?.message || "");
  if (code.includes("permission-denied") || message.toLowerCase().includes("insufficient permissions")) {
    return "League data access is blocked by Firestore rules. Deploy the included firestore.rules file.";
  }
  return message || "Unable to load Matrix League.";
}

export default function useLeagueData() {
  const { claims } = useAuth();
  const orgId = String(claims?.orgId || "").trim();
  const [season, setSeason] = useState(DEFAULT_SEASON);
  const [clubs, setClubs] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      setError("No organization is attached to this account.");
      return undefined;
    }

    let mounted = true;
    let seasonSeen = false;
    setLoading(false);
    setError("");

    // Subscribe immediately so authorized users enter League without a blocking
    // provisioning screen. If the season does not exist yet, render the local
    // default instantly and create it in the background.
    const stopSeason = subscribeSeason(orgId, DEFAULT_SEASON.id, (value) => {
      if (!mounted) return;
      seasonSeen = true;
      setSeason(value || DEFAULT_SEASON);
      if (!value) {
        ensureLeagueSeason(orgId, DEFAULT_SEASON.id).catch((err) => {
          if (mounted) setError(friendlyLeagueError(err));
        });
      }
    }, (err) => {
      if (!mounted) return;
      setError(friendlyLeagueError(err));
    });

    const stopClubs = subscribeClubs(orgId, setClubs, (err) => {
      if (!mounted) return;
      setError(friendlyLeagueError(err));
    });

    const stopCompetitions = subscribeCompetitions(orgId, setCompetitions, (err) => {
      if (!mounted) return;
      setError(friendlyLeagueError(err));
    });

    // Defensive background initialization in case the first snapshot is slow.
    const timer = window.setTimeout(() => {
      if (!mounted || seasonSeen) return;
      ensureLeagueSeason(orgId, DEFAULT_SEASON.id).catch((err) => {
        if (mounted) setError(friendlyLeagueError(err));
      });
    }, 750);

    return () => {
      mounted = false;
      window.clearTimeout(timer);
      stopSeason();
      stopClubs();
      stopCompetitions();
    };
  }, [orgId]);


  useEffect(() => {
    if (!orgId || !season?.id || season.status !== "live" || !season.endDate || !clubs.length) return;
    finalizeExpiredSeason(orgId, season, clubs, competitions).catch((err) => setError(friendlyLeagueError(err)));
  }, [orgId, season, clubs, competitions]);

  const standings = useMemo(() => buildStandings(clubs, season, competitions), [clubs, season, competitions]);
  return { orgId, season, clubs, competitions, standings, loading, error };
}
